import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { Container, TextField, Button, Typography, Box, Paper, IconButton, CircularProgress, Alert, Snackbar } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Header from "./MenuBar";
import Footer from "./Footer";
import MyDocument from "./MyDocument";
import { useNavigate } from "react-router-dom";
import "./Style.css";
const envVariable = require("../EnvironmentVariables.json");

const GenerateInvoice = () => {

  const SC_Id = localStorage.getItem("SC_Id");
  const { taskId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const customerName = location.state?.customerName ?? "Customer";
  const userCarId = location.state.carId;
  const customerID = location.state?.customerId ?? 0;
  const numInvoices = location.state?.numInvoices ?? 0;

  const [VIN, setVIN] = useState(location.state?.vin_number ?? "");
  const [taxPercentage, setTaxPercentage] = useState("");
  const [comments, setComments] = useState("");
  const [labourCharges, setLabourCharges] = useState([]);
  const [parts, setParts] = useState([]);
  const [customCharges, setCustomCharges] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [billingFrom, setBillingFrom] = useState(null);
  const [billingTo, setBillingTo] = useState(null);
  const [taskTimeSeconds, setTaskTimeSeconds] = useState(() => Number(location.state?.time ?? 0));
  const [taskTime, setTaskTime] = useState({ days: 0, hours: 0, minutes: 0 });
  const [vinPresent, setVinPresent] = useState(true);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [showSupersedWarning, setShowSupersedWarning] = useState(false);
  const [hasPaidInvoice, setHasPaidInvoice] = useState(false);

  const formatDuration = (totalSeconds) => {
    const safeSeconds = Math.max(0, parseInt(totalSeconds || 0, 10));
    const durationDays = Math.floor(safeSeconds / 86400);
    const durationHours = Math.floor((safeSeconds % 86400) / 3600);
    const durationMinutes = Math.floor((safeSeconds % 3600) / 60);

    const parts = [];
    if (durationDays) parts.push(`${durationDays} day${durationDays === 1 ? "" : "s"}`);
    if (durationHours) parts.push(`${durationHours} hr${durationHours === 1 ? "" : "s"}`);
    if (durationMinutes) parts.push(`${durationMinutes} min${durationMinutes === 1 ? "" : "s"}`);
    if (parts.length === 0) parts.push(`0 mins`);

    return parts.join(" ");
  };

  // Loading and notification states.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" // "success" | "error" | "warning" | "info"
  });

  /* ---------- time setup ---------- */
  useEffect(() => {
    const safeSeconds = Math.max(0, parseInt(taskTimeSeconds || 0, 10));
    const days = Math.floor(safeSeconds / 86400);
    const hours = Math.floor((safeSeconds % 86400) / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    setTaskTime({ days, hours, minutes });
    if (VIN === "") {
      setVinPresent(false);
    }
  }, [taskTimeSeconds, VIN]);

  useEffect(() => {
    if (Number(location.state?.time ?? 0) > 0) {
      return;
    }

    const fetchTaskTime = async () => {
      try {
        const response = await fetch(`${envVariable.serverId}/SCO/updateTask.php?TaskId=${taskId}`);
        const data = await response.json();

        if (data.status === "Success") {
          setTaskTimeSeconds(Number(data.data?.task?.accumulated_secs ?? 0));
        }
      } catch (error) {
        console.error("Error fetching task time:", error);
      }
    };

    fetchTaskTime();
  }, [taskId, location.state?.time]);

  useEffect(() => {
    const checkExistingInvoice = async () => {
      try {
        const response = await fetch(
          `${envVariable.serverId}/Payment/getInvoiceHistory.php?task_id=${taskId}`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch invoice history: ${response.status}`);
        }

        const data = await response.json();
        if (data.status === "success") {
          setHasPaidInvoice(Boolean(data.has_paid_invoice));

          if (data.active_invoice_id) {
            const activeId = Number(data.active_invoice_id);
            const activeInvoice = Array.isArray(data.invoices)
              ? data.invoices.find((inv) => Number(inv.invoice_id) === activeId)
              : null;

            setExistingInvoice(activeInvoice ?? null);
            setShowSupersedWarning(Boolean(activeInvoice));
          } else {
            setExistingInvoice(null);
            setShowSupersedWarning(false);
          }
        }
      } catch (error) {
        console.error("Error checking existing invoice:", error);
      }
    };

    checkExistingInvoice();
  }, [taskId]);

  //helper

  const addLabourCharge = () => {
    setLabourCharges([...labourCharges, { description: "", cost: "" }]);
  };

  const addPart = () => {
    setParts([...parts, { number: "", information: "", cost: "" }]);
  };

  const addCustomCharge = () => {
    setCustomCharges([...customCharges, { title: "", cost: "" }]);
  };

  const handleLabourChargeChange = (index, field, value) => {
    const updatedCharges = labourCharges.map((charge, i) =>
      i === index ? { ...charge, [field]: value } : charge
    );
    setLabourCharges(updatedCharges);
  };

  const handlePartChange = (index, field, value) => {
    const updatedParts = parts.map((part, i) =>
      i === index ? { ...part, [field]: value } : part
    );
    setParts(updatedParts);
  };

  const removeLabourCharge = (index) => {
    setLabourCharges((prev) => prev.filter((_, i) => i !== index));
  };

  const removePart = (index) => {
    setParts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCustomChargeChange = (index, field, value) => {
    const updatedCharges = customCharges.map((charge, i) =>
      i === index ? { ...charge, [field]: value } : charge
    );
    setCustomCharges(updatedCharges);
  };

  const removeCustomCharge = (index) => {
    setCustomCharges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

   //billing info
  const fetchBillingInfo = async () => {
    try {
      const responseFrom = await fetch(
        `${envVariable.serverId}/SCO/getBillingFrom.php?SC_Id=${SC_Id}`
      );
      const responseTo = await fetch(
        `${envVariable.serverId}/SCO/getBillingTo.php?taskId=${taskId}&customerId=${customerID}`
      );

      if (!responseFrom.ok || !responseTo.ok) {
        throw new Error("Failed to fetch billing data");
      }

      const dataFrom = await responseFrom.json();
      const dataTo = await responseTo.json();

      setBillingFrom(dataFrom);
      setBillingTo(dataTo);

      // Return the data so handleSubmit can use it immediately
      return { dataFrom, dataTo };
    } catch (error) {
      console.error("Error fetching billing info:", error.message);
      return { dataFrom: null, dataTo: null };
    }
  };

   //preview
  const handlePreview = async () => {
    await fetchBillingInfo();
    setShowPreview(true);
  };

 //on Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (hasPaidInvoice) {
      setSnackbar({
        open: true,
        message: "Cannot create invoice. This task has already been paid.",
        severity: "error"
      });
      return;
    }

    if (showSupersedWarning && existingInvoice) {
      const confirmed = window.confirm(
        `This will replace the existing invoice v${existingInvoice?.version ?? "?"}. Continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    // Start loading
    setIsSubmitting(true);

    let from = billingFrom;
    let to = billingTo;

    if (!from || !to) {
      // If missing, fetch it now
      const { dataFrom, dataTo } = await fetchBillingInfo();
      from = dataFrom;
      to = dataTo;

      // Wait a tick for state to settle before rendering PDF
      await new Promise((r) => setTimeout(r, 0));
    }

    // 2) Build PDF document
  const doc = (
      <MyDocument
        data={{
          VIN,
          taxPercentage,
          comments,
          labourCharges,
          parts,
          customCharges,
          taskTime,
          taskTimeSeconds,
          formattedTaskTime: formatDuration(taskTimeSeconds),
          invoiceNumber: "1234",
          billingFrom: from,
          billingTo: to,
        }}
      />
    );

    // 3) Calculate amounts for backend (including custom charges)
    const subtotal =
      labourCharges.reduce((sum, c) => sum + parseFloat(c.cost || 0), 0) +
      parts.reduce((sum, p) => sum + parseFloat(p.cost || 0), 0) +
      customCharges.reduce((sum, c) => sum + parseFloat(c.cost || 0), 0);
    const tax = subtotal * (parseFloat(taxPercentage) / 100);
    const total = parseFloat((subtotal + tax).toFixed(2));

    try {
      // 4) Generate PDF blob
      const blob = await pdf(doc).toBlob();
      if (!(blob instanceof Blob)) throw new Error("Blob generation failed");

      // 5) Build multipart payload
      const formData = new FormData();
      formData.append("file", blob, `invoice${taskId}${numInvoices}.pdf`);
      formData.append("sc_id", SC_Id);
      formData.append("task_id", taskId);
      formData.append("VIN", VIN);
      formData.append("tax", taxPercentage);
      formData.append("comments", comments);
      formData.append("sco_id", localStorage.getItem("User_Id"));
      formData.append("customer_id", customerID);
      formData.append("total", total);

      // 6) Upload invoice
      const uploadResponse = await fetch(
        `${envVariable.serverId}/Chat/invoiceUpload.php`,
        {
          method: "POST",
          body: formData,
        }
      );

      const uploadData = await uploadResponse.json().catch(() => null);

      if (!uploadResponse.ok || !uploadData || uploadData.type !== "success") {
        if (uploadData?.error_code === "INVOICE_ALREADY_PAID") {
          setHasPaidInvoice(true);
        }
        throw new Error(
          (uploadData && uploadData.message) ||
            `Failed to save invoice. Status: ${uploadResponse.status}`
        );
      }

      // 7) Create Stripe session
      const invoiceId = uploadData.invoice_id;
      try {
        const stripeRes = await fetch(
          `${envVariable.serverId}/Payment/createCheckoutSession.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ invoice_id: invoiceId }),
          }
        );
        const stripeData = await stripeRes.json();
        if (stripeData.status !== "success") {
          console.warn("Stripe session failed:", stripeData.error);
        }
      } catch (stripeError) {
        console.error("Error creating Stripe session:", stripeError);
      }

      // 8) Update VIN if missing
      if (!vinPresent) {
        await fetch(`${envVariable.serverId}/users/updateCarVIN.php`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_car_id: userCarId,
            vin: VIN,
          }),
        });
      }

      // 9) Notification
      await fetch(`${envVariable.serverId}/SCO/createNotification.php`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: customerID,
          task_id: taskId,
          notif_type: "Invoice Created",
          description: total.toFixed(2),
          name: localStorage.getItem("sc_name"),
        }),
      });

      // 10) Show success message
      setSnackbar({
        open: true,
        message: "Invoice created successfully! Redirecting to invoice history...",
        severity: "success"
      });

      // 11) Navigate to invoice history after short delay
      setTimeout(() => {
        navigate(`/tasks/invoice-history/${taskId}`, {
          state: {
            customerId: customerID,
            customerName: customerName,
            originDate: location.state?.originDate ?? null,
            vin_number: VIN,
            time: taskTimeSeconds,
            carId: userCarId,
          },
        });
      }, 1500);

    } catch (error) {
      console.error("Error submitting invoice:", error);

      // Show error message
      setSnackbar({
        open: true,
        message: `Failed to create invoice: ${error.message || "Unknown error occurred"}`,
        severity: "error"
      });
    } finally {
      // Stop loading
      setIsSubmitting(false);
    }
  };

  /* ------------------------------------------------------------------
     render
  ------------------------------------------------------------------ */
  return (
    <>
      <Header />
      <Container maxWidth="md">
        <Typography variant="h4" gutterBottom>
          Generate Invoice
        </Typography>
        <Typography variant="subtitle1">
          Total time put into the task: {formatDuration(taskTimeSeconds)}
        </Typography>

        {showSupersedWarning && existingInvoice && !hasPaidInvoice && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Active Invoice Exists</strong>
            <br />
            An active invoice (v{existingInvoice.version}) for $
            {Number(existingInvoice.total || 0).toFixed(2)} already exists.
            Submitting this form will replace the previous invoice.
            <Button
              size="small"
              onClick={() => navigate(`/tasks/invoice-history/${taskId}`)}
              sx={{ ml: 2 }}
            >
              View Invoice History
            </Button>
          </Alert>
        )}

        {hasPaidInvoice && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Cannot Create New Invoice</strong>
            <br />
            This task has a paid invoice. New invoices cannot be created.
          </Alert>
        )}

        {/* ---------- FORM ---------- */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="VIN"
            value={VIN}
            onChange={(e) => setVIN(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Enter VIN"
          />
          <TextField
            label="Tax Percentage"
            value={taxPercentage}
            onChange={(e) => setTaxPercentage(e.target.value)}
            type="number"
            fullWidth
            margin="normal"
            placeholder="Enter Tax Percentage"
          />
          <TextField
            label="Comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            placeholder="Enter any comments"
          />

          {/* Labour charges */}
          {labourCharges.map((charge, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{ position: "relative", p: 2, pt: 3, pr: 4, mb: 2 }}
            >
              <IconButton
                aria-label="Remove labour charge"
                size="small"
                onClick={() => removeLabourCharge(index)}
                sx={{ position: "absolute", top: 10, right: 12 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <TextField
                label="Labour Cost Description"
                value={charge.description}
                onChange={(e) =>
                  handleLabourChargeChange(index, "description", e.target.value)
                }
                fullWidth
                margin="normal"
              />
              <TextField
                label="Cost"
                value={charge.cost}
                onChange={(e) =>
                  handleLabourChargeChange(index, "cost", e.target.value)
                }
                type="number"
                fullWidth
                margin="normal"
              />
            </Paper>
          ))}

          {/* Parts */}
          {parts.map((part, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{ position: "relative", p: 2, pt: 3, pr: 4, mb: 2 }}
            >
              <IconButton
                aria-label="Remove part"
                size="small"
                onClick={() => removePart(index)}
                sx={{ position: "absolute", top: 10, right: 12 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <TextField
                label="Part Number"
                value={part.number}
                onChange={(e) =>
                  handlePartChange(index, "number", e.target.value)
                }
                fullWidth
                margin="normal"
              />
              <TextField
                label="Part Information"
                value={part.information}
                onChange={(e) =>
                  handlePartChange(index, "information", e.target.value)
                }
                fullWidth
                margin="normal"
              />
              <TextField
                label="Part Cost"
                value={part.cost}
                onChange={(e) =>
                  handlePartChange(index, "cost", e.target.value)
                }
                type="number"
                fullWidth
                margin="normal"
              />
            </Paper>
          ))}

          {/* Custom Charges */}
          {customCharges.map((charge, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{ position: "relative", p: 2, pt: 3, pr: 4, mb: 2, backgroundColor: "#f5f5f5" }}
            >
              <IconButton
                aria-label="Remove custom charge"
                size="small"
                onClick={() => removeCustomCharge(index)}
                sx={{ position: "absolute", top: 10, right: 12 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <TextField
                label="Custom Charge Title"
                value={charge.title}
                onChange={(e) =>
                  handleCustomChargeChange(index, "title", e.target.value)
                }
                fullWidth
                margin="normal"
                placeholder="E.g., Disposal Fee, Environmental Charge"
              />
              <TextField
                label="Cost"
                value={charge.cost}
                onChange={(e) =>
                  handleCustomChargeChange(index, "cost", e.target.value)
                }
                type="number"
                fullWidth
                margin="normal"
              />
            </Paper>
          ))}

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", my: 2, flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addLabourCharge}
            >
              Add Labour Charge
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addCustomCharge}
              sx={{ backgroundColor: "#e3f2fd" }}
            >
              Add Custom Charge
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addPart}
            >
              Add Part
            </Button>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", my: 2 }}>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={isSubmitting || hasPaidInvoice}
            >
              View
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                minWidth: 120,
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>

          {/* Preview */}
          {showPreview && (
            <PDFViewer width="100%" height="600">
              <MyDocument
                data={{
                  VIN,
                  taxPercentage,
                  comments,
                  labourCharges,
                  parts,
                  customCharges,
                  invoiceNumber: "1234",
                  billingFrom,
                  billingTo,
                }}
              />
            </PDFViewer>
          )}
        </Box>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
      <Footer />
    </>
  );
};

export default GenerateInvoice;
